import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { FieldProps } from "../../../Reusables/components/InputFieldsController/InputFieldsController.utils";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

export interface FileInputTypes {
  FirmReferenceDataId: number;
  ReferenceDataCode: string;
  ReferenceDataDescription: string;
  ReferenceDataDomain: string;
  ReferenceDataValue: string;
  CreatedDate: string;
  CreatedBy: string;
  FieldName: string;
  FieldType: string;
  FieldCode: string;
  CategoryCode: string;
}

export interface ClientRecord {
  ClientMasterId: number;
  ClientId: string;
  "Client Name": string;
  ClientName?: string;
  AttestFormId: number;
  AttestFormName: string;
  AttestFormStatus: string;
  OnboardingFormId: number;
  OnboardingFormName: string;
  OnboardingFormStatus: string;
  TaxAcceptanceFormId: number;
  TaxAcceptanceFormName: string;
  TaxAcceptanceFormStatus: string;
  AuditAcceptanceFormId: number;
  AuditAcceptanceFormName: string;
  AuditAcceptanceFormStatus: string;
  ClientTypeCode: string;
  ClientTypeName: string;
  "Approval Status": string;
  BackgroundCheckStatus: string;
  IsPublic: string;
  BusinessUnit: string;
  EngagementLetterStatus?: string;
  Active: boolean;
  "Last Updated Date"?: string;
  "Created Date"?: string;
  FirmDetailId?: number | undefined;
  ClientDetailsPdfUrl?: string; // URL for approval PDF, if available
  CCHCreationFailed?: boolean | number | string;
  CCHFailureCode?: string;
  CCHFailureStep?: string;
  CCHFailureLabel?: string;
  CCHFailureMessage?: string;
  EgressStatus?: string;
  IngressStatus?: string;
  COIApprovalStatus?: string | null;
  MyPendingCOIReview?: "Y" | "N";
  COIApprover?: "Y" | "N" | null;
}

const isFailStatus = (status?: string) =>
  String(status ?? "").trim().toUpperCase() === "FAIL";

type CchFailureFields = Pick<
  ClientRecord,
  | "CCHCreationFailed"
  | "CCHFailureCode"
  | "CCHFailureStep"
  | "CCHFailureLabel"
  | "CCHFailureMessage"
  | "EgressStatus"
  | "IngressStatus"
>;

const cchFailureLabels: Record<string, string> = {
  CLIENT_CREATION: "CCH client creation failed",
  CLIENT_CREATE: "CCH client creation failed",
  EMAIL_CREATION: "CCH email creation failed",
  EMAIL_CREATE: "CCH email creation failed",
  PHONE_CREATION: "CCH phone creation failed",
  PHONE_CREATE: "CCH phone creation failed",
  DOCUMENT_UPLOAD: "Document upload failed",
  STAFF_ASSIGNMENT: "Staff assignment failed",
};

const normalizeFailureCode = (value?: string) =>
  String(value ?? "")
    .trim()
    .replace(/[\s-]+/g, "_")
    .toUpperCase();

export const hasCchCreationFailure = (row?: CchFailureFields | null) => {
  if (!row) return false;

  const failedFlag = String(row.CCHCreationFailed ?? "").trim().toUpperCase();

  return (
    !!row.CCHFailureCode ||
    !!row.CCHFailureStep ||
    !!row.CCHFailureLabel ||
    !!row.CCHFailureMessage ||
    row.CCHCreationFailed === true ||
    row.CCHCreationFailed === 1 ||
    failedFlag === "Y" ||
    failedFlag === "TRUE" ||
    failedFlag === "FAIL" ||
    isFailStatus(row.EgressStatus) ||
    isFailStatus(row.IngressStatus)
  );
};

export const getCchFailureLabel = (row?: CchFailureFields | null) => {
  if (!row) return "CCH processing failed";

  const explicitLabel = String(row.CCHFailureLabel ?? "").trim();
  if (explicitLabel) return explicitLabel;

  const code = normalizeFailureCode(row.CCHFailureCode || row.CCHFailureStep);
  if (code && cchFailureLabels[code]) return cchFailureLabels[code];

  if (code) {
    return `${code
      .toLowerCase()
      .split("_")
      .filter(Boolean)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(" ")} failed`;
  }

  return "CCH processing failed";
};

export const getCchCreationFailureTooltip = (row?: CchFailureFields | null) => {
  return `${getCchFailureLabel(row)}. Please check the Audit Trail for details.`;
};

export interface SelectOption {
  id?: string;
  label: string;
  value: string;
}

export interface InvalidClient {
  ClientMasterId: number;
  ClientName: string;
  missingDocuments: string[];
  reason?: string; // "MISSING_DOCUMENTS" | "NO_APPROVER";
  message?: string;
}

export interface SendForApprovalResponseData {
  processedClients: Array<{ ClientMasterId: number; ClientName: string }>;
  invalidClients: InvalidClient[];
  totalRequested: number;
  totalProcessed: number;
  totalInvalid: number;
}

export interface SendForApprovalResponse {
  success: boolean;
  message: string;
  data: SendForApprovalResponseData;
}

/**
 * Returns the grid column title as-is from the API response.
 * Meant to be a placeholder if title formatting is added later.
 *
 * @param defaultName - The default name from the API.
 * @returns The final column name to be used in the grid.
 */
export const GridColumnTitleRenderer = (defaultName: string) => {
  return defaultName;
};

/**
 * Formats a given timestamp using Day.js.
 *
 * @param params.value - The ISO timestamp string to format.
 * @param params.showTime - Whether to include the time part in the output (default: true).
 * @returns Formatted date or date+time string.
 */
export const TimeRenderer = (params: {
  value?: string;
  showTime?: boolean;
}) => {
  const { value, showTime = true } = params;

  if (!value) return "";

  dayjs.extend(utc);
  dayjs.extend(timezone);
  
  const localTime = dayjs.utc(value).tz(dayjs.tz.guess());

  const formattedDate = localTime.format("MM/DD/YYYY");
  const formattedTime = localTime.format("hh:mm:ss A");

  return showTime ? `${formattedDate} ${formattedTime}` : formattedDate;
};

/**
 * A hook that returns a debounced version of a value.
 * Useful for search inputs or any rapidly changing value that needs throttling.
 *
 * @param value - The input value to debounce.
 * @param delay - The debounce delay in milliseconds.
 * @returns The debounced value.
 */
export const useDebounce = <T>(value: T, delay: number): T => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);

  return debouncedValue;
};

export type PurposeOption = {
  code: string | undefined;
  label: string; // e.g., "Tax", "Audit", "CAS", "Business Management", "Advisory"
  value?: string; // whatever the API sends; not relied on for flow
};

export type FlowStep = {
  key: "CLIENT_ONBOARDING" | "CLIENT_TAX_ACCEPTANCE" | "CLIENT_AUDIT_ACCEPTANCE" | "DOCUMENTS" | "CLIENT_ATTEST" | "COI_RESULTS"; // extend if needed
  label: string; // purely cosmetic
  idField:
    | "OnboardingFormId"
    | "TaxAcceptanceFormId"
    | "AuditAcceptanceFormId"
    | "DocumentsUploadId"
    | "AttestFormId"
    | "COIApprovalStatus";
  enabled: boolean;
  order: number;
};

// Not part of FLOW_BY_PURPOSE: ClientEditor/ClientCreator consume the same
// config for their own tab sets, which have no renderer for this key. Only
// ClientApprovalWizard imports and appends this locally to its own flow.
export const COI_RESULTS_STEP: FlowStep = {
  key: "COI_RESULTS",
  label: "COI Results",
  idField: "COIApprovalStatus",
  enabled: true,
  order: 3,
};

/** 1) Minimal flow config. Labels are yours to change; keys drive routing. */
const FLOW_BY_PURPOSE: Record<string, FlowStep[]> = {
  tax: [
    {
      key: "CLIENT_ONBOARDING",
      label: "Intake",
      idField: "OnboardingFormId",
      enabled: true,
      order: 1,
    },
    {
      key: "CLIENT_TAX_ACCEPTANCE",
      label: "Evaluate",
      idField: "TaxAcceptanceFormId",
      enabled: true,
      order: 2,
    },
    {
      key: "DOCUMENTS",
      label: "Documents",
      idField: "DocumentsUploadId",
      enabled: true,
      order: 3,
    },
  ],
  "client accounting services": [
    {
      key: "CLIENT_ONBOARDING",
      label: "Intake",
      idField: "OnboardingFormId",
      enabled: true,
      order: 1,
    },
    {
      key: "CLIENT_TAX_ACCEPTANCE",
      label: "Evaluate",
      idField: "TaxAcceptanceFormId",
      enabled: true,
      order: 2,
    },
    {
      key: "DOCUMENTS",
      label: "Documents",
      idField: "DocumentsUploadId",
      enabled: true,
      order: 3,
    },
  ],
  "business management": [
    {
      key: "CLIENT_ONBOARDING",
      label: "Intake",
      idField: "OnboardingFormId",
      enabled: true,
      order: 1,
    },
    {
      key: "CLIENT_TAX_ACCEPTANCE",
      label: "Evaluate",
      idField: "TaxAcceptanceFormId",
      enabled: true,
      order: 2,
    },
    {
      key: "DOCUMENTS",
      label: "Documents",
      idField: "DocumentsUploadId",
      enabled: true,
      order: 3,
    },
  ],
  advisory: [
    {
      key: "CLIENT_ONBOARDING",
      label: "Intake",
      idField: "OnboardingFormId",
      enabled: true,
      order: 1,
    },
    {
      key: "CLIENT_TAX_ACCEPTANCE",
      label: "Evaluate",
      idField: "TaxAcceptanceFormId",
      enabled: true,
      order: 2,
    },
    {
      key: "DOCUMENTS",
      label: "Documents",
      idField: "DocumentsUploadId",
      enabled: true,
      order: 3,
    },
  ],
  "assurance": [
    {
      key: "CLIENT_ATTEST",
      label: "Intake",
      idField: "AttestFormId",
      enabled: true,
      order: 1,
    },
    {
      key: "CLIENT_AUDIT_ACCEPTANCE",
      label: "Evaluate",
      idField: "AuditAcceptanceFormId",
      enabled: true,
      order: 2,
    },
    {
      key: "DOCUMENTS",
      label: "Documents",
      idField: "DocumentsUploadId",
      enabled: true,
      order: 3,
    },
    
    // {
    //   key: "CLIENT_ONBOARDING",
    //   label: "Onboard",
    //   idField: "OnboardingFormId",
    //   enabled: true,
    //   order: 4,
    // },
    // If you truly need a second attest, give it a UNIQUE key:
    // { key: "attest_review", label: "Attest Review", idField: "AttestFormId", enabled: true, order: 4 },
  ],
  "tax & acct clients": [
    {
      key: "CLIENT_ONBOARDING",
      label: "Intake",
      idField: "OnboardingFormId",
      enabled: true,
      order: 1,
    },
    {
      key: "CLIENT_TAX_ACCEPTANCE",
      label: "Evaluate",
      idField: "TaxAcceptanceFormId",
      enabled: false,
      order: 2,
    },
    {
      key: "DOCUMENTS",
      label: "Documents",
      idField: "DocumentsUploadId",
      enabled: true,
      order: 3,
    },
  ],
  "gyt": [
    {
      key: "CLIENT_ONBOARDING",
      label: "Intake",
      idField: "OnboardingFormId",
      enabled: true,
      order: 1,
    },
    {
      key: "CLIENT_TAX_ACCEPTANCE",
      label: "Evaluate",
      idField: "TaxAcceptanceFormId",
      enabled: false,
      order: 2,
    },
    {
      key: "DOCUMENTS",
      label: "Documents",
      idField: "DocumentsUploadId",
      enabled: true,
      order: 3,
    },
  ]
};

export type Purpose = 'tax' | 'assurance' | 'client accounting services' | 'advisory' | 'business management' | 'tax & acct clients' | 'gyt';

/**
 * Resolves purpose from the single BusinessUnit field.
 * Expects values like "Tax", "Audit", "CAS", "Advisory", "Business Management".
 */
export function resolvePurposeFromFields(fields?: FieldProps[]): Purpose | undefined {
  if (!fields?.length) return undefined;

  const f = fields.find(x => {
    const name = (x.allfields_code || x.allfields_key || x.label || '').toLowerCase();
    return name === 'business_unit';
  });
  if (!f) return undefined;

  const raw = (
    typeof f.value === 'string' ? f.value :
    Array.isArray(f.value) ? String(f.value[0] ?? '') :
    typeof f.value === 'object' && f.value ? String((f.value as any).value ?? (f.value as any).label ?? (f.value as any).name ?? '') :
    String(f.value ?? '')
  ).trim().toLowerCase();

  const key = raw.replace(/[\s_-]/g, ''); // "business management" -> "businessmanagement"
  const map: Record<string, Purpose> = {
    tax: 'tax',
    assurance: 'assurance',
    clientaccountingservices: 'client accounting services',
    advisory: 'advisory',
    businessmanagement: 'business management',
    'tax & acct clients': 'tax & acct clients',
    gyt: 'gyt',
  };

  return map[key];
}

/** 3) Public helpers */
export const getFormFlowByPurpose = (purpose?: string): FlowStep[] =>
  (purpose ? FLOW_BY_PURPOSE[purpose] : [
    {
      key: "CLIENT_ONBOARDING",
      label: "Intake",
      idField: "OnboardingFormId",
      enabled: true,
      order: 1,
    },
    {
      key: "CLIENT_TAX_ACCEPTANCE",
      label: "Evaluate",
      idField: "TaxAcceptanceFormId",
      enabled: true,
      order: 2,
    },
    {
      key: "DOCUMENTS",
      label: "Documents",
      idField: "DocumentsUploadId",
      enabled: true,
      order: 3,
    },
  ]) ?? [
    {
      key: "CLIENT_ONBOARDING",
      label: "Intake",
      idField: "OnboardingFormId",
      enabled: true,
      order: 1,
    },
    {
      key: "CLIENT_TAX_ACCEPTANCE",
      label: "Evaluate",
      idField: "TaxAcceptanceFormId",
      enabled: true,
      order: 2,
    },
    {
      key: "DOCUMENTS",
      label: "Documents",
      idField: "DocumentsUploadId",
      enabled: true,
      order: 3,
    },
  ];

export const getFormFlowForFields = (fields?: FieldProps[]): FlowStep[] => {
  const p = resolvePurposeFromFields(fields);
  return getFormFlowByPurpose(p);
};

/** Optional: hybrid helper (prefer explicit purpose prop, else derive from fields) */
export const getFormFlow = (
  explicitPurpose?: PurposeOption | null,
  fields?: FieldProps[]
): FlowStep[] => {
  const purposeKey = explicitPurpose?.value?.toLowerCase() ?? resolvePurposeFromFields(fields);
  return getFormFlowByPurpose(purposeKey);
};

export type FirstPageCacheEntry = {
    records: any[];
    totalCount: number;
    savedAt: number;
  };
